import { Col, Popover, Row } from 'antd';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as IconArrow } from '../../icons/icon-arrow.svg';
import { ReactComponent as IconCloseMenu } from '../../icons/icon-close.svg';
import { RootStoresContext } from '../../stores/RootStore';
import { cleanString } from '../../util/renderHelpers';

type NavLinkProps = {
  name: string;
  path: string;
  heading: string;
  cls: string;
  children?: never;
  clickHandler: () => void;
};

const NavLink: React.FC<NavLinkProps> = ({ name, path, cls, heading, clickHandler }) => (
  <Link
    to={path}
    title={cleanString(name)}
    onClick={clickHandler}
    className={`step__navigation-link ${cls}`}
  >
    <span className="step__navigation-icon">
      <IconArrow />
    </span>
    <div className="step__navigation-content">
      <div>
        {heading && <span className="step__navigation-heading">{heading}</span>}
        <span className="step__navigation-name">{cleanString(name)}</span>
      </div>
    </div>
    <span />
  </Link>
);

const PrevNextNav = observer(() => {
  const rootStore = useContext(RootStoresContext);
  const [visibleNext, visibleChangeNext] = useState(false);
  const [visiblePrev, visibleChangePrev] = useState(false);

  const handleVisibleChangeNext = (v: boolean) => visibleChangeNext(v);
  const hideNext = () => visibleChangeNext(false);

  const handleVisibleChangePrev = (v: boolean) => visibleChangePrev(v);
  const hidePrev = () => visibleChangePrev(false);

  const currentStepId = rootStore.pageStore.currentPage && rootStore.pageStore.currentPage.pageId;
  const prevNav =
    currentStepId &&
    toJS(rootStore.stepsStore.currentSteps) &&
    rootStore.stepsStore.getParents(currentStepId);
  const nextNav =
    currentStepId &&
    toJS(rootStore.stepsStore.currentSteps) &&
    rootStore.stepsStore.getChildren(currentStepId);

  const displayPrevNav =
    prevNav &&
    prevNav.map(
      prev =>
        prev && (
          <NavLink
            key={`prev-nav-${prev.stepId}`}
            path={prev.stepPath}
            name={prev.stepName}
            heading={`${prevNav.length > 1 ? '' : 'Previous step:'}`}
            cls="prev"
            clickHandler={hidePrev}
          />
        )
    );

  const displayNextNav =
    nextNav &&
    nextNav.map(next => (
      <NavLink
        key={`next-nav-${next.stepId}`}
        path={next.stepPath}
        name={next.stepName}
        heading={`${nextNav.length > 1 ? '' : 'Next step:'}`}
        cls="next"
        clickHandler={hideNext}
      />
    ));

  const displayNextNavPop = (
    <Popover
      overlayClassName="step__navigation__multiple"
      trigger="click"
      placement="bottomRight"
      visible={visibleNext}
      onVisibleChange={handleVisibleChangeNext}
      content={
        <>
          <button
            aria-label="Close Multiple Strp Navigation"
            className="step__navigation-close next"
            onClick={hideNext}
          >
            <IconCloseMenu />
          </button>
          <span className="step__navigation__multiple-heding">
            Multiple next steps - please choose
          </span>
          {displayNextNav}
        </>
      }
    >
      <button
        aria-label="Open Next Multiple Strp Navigation"
        className={`step__navigation__multiple-button${visibleNext ? ' hide' : ''}`}
      >
        Multiple next steps - please choose
        <span className="step__navigation-icon next">
          <IconArrow />
        </span>
      </button>
    </Popover>
  );

  const displayPrevNavPop = (
    <Popover
      overlayClassName="step__navigation__multiple"
      trigger="click"
      placement="bottomLeft"
      visible={visiblePrev}
      onVisibleChange={handleVisibleChangePrev}
      content={
        <>
          <button
            aria-label="Close Multiple Strp Navigation"
            className="step__navigation-close prev"
            onClick={hidePrev}
          >
            <IconCloseMenu />
          </button>
          <span className="step__navigation__multiple-heding">
            Multiple prev steps - please choose
          </span>
          {displayPrevNav}
        </>
      }
    >
      <button
        aria-label="Open Previous Multiple Strp Navigation"
        className={`step__navigation__multiple-button${visiblePrev ? ' hide' : ''}`}
      >
        <span className="step__navigation-icon prev">
          <IconArrow />
        </span>
        Multiple previous steps - please choose
      </button>
    </Popover>
  );

  return currentStepId && (displayPrevNav || displayNextNav) ? (
    <div className="step__navigation">
      <Row className="step__navigation-wrapper" type="flex" gutter={{ xs: 8 }}>
        <Col xs={{ span: 12 }} className="step__navigation-prev">
          {displayPrevNav && displayPrevNav.length > 1 ? displayPrevNavPop : displayPrevNav}
        </Col>
        <Col xs={{ span: 12 }} className="step__navigation-next">
          {displayNextNav && displayNextNav.length > 1 ? displayNextNavPop : displayNextNav}
        </Col>
      </Row>
    </div>
  ) : null;
});

export default PrevNextNav;
